import * as React from "react";
import { isMobileBuild } from "@/platform";

/**
 * Viewport size hook driving responsive layout decisions in AppShell
 * (sidebar vs bottom-bar) and per-page renders (cards vs table on
 * Inbox / Alarms / Tickets).  Single source of truth so we don't
 * scatter `window.innerWidth` reads or duplicate matchMedia listeners
 * across pages.
 *
 * Breakpoints match the page-spec responsive matrices (e.g.
 * 04 inbox §4.5, 05 alarms §4.5, 06 tickets §4.6) and Tailwind's
 * defaults so utility classes line up with hook output:
 *
 *   <  768 (md)  → mobile  — single-column lists, bottom-bar nav,
 *                            preview as bottom-sheet
 *   ≥ 768 < 1024 → tablet  — compact tables with column-shedding,
 *                            sidebar forced to icon-only
 *   ≥ 1024 (lg)  → desktop — full layout, sidebar + drawer / panel
 *                            previews
 *
 * On mobile builds (Capacitor), always returns "mobile" regardless of
 * physical screen size — iPad 13"+ would otherwise get the desktop
 * layout because its viewport width exceeds the 1024px breakpoint.
 *
 * The hook is SSR-safe (returns "desktop" when window is undefined)
 * and reactive (matchMedia listeners trigger re-renders on resize).
 */
export type ViewportSize = "mobile" | "tablet" | "desktop";

const MOBILE_MAX = 767; // < md
const TABLET_MAX = 1023; // < lg

function readSize(): ViewportSize {
  // Mobile builds must always render the mobile layout regardless of
  // physical screen size (iPad 13"+ would otherwise exceed the 1024px
  // desktop breakpoint and show the sidebar / web view).
  if (isMobileBuild) return "mobile";
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w <= MOBILE_MAX) return "mobile";
  if (w <= TABLET_MAX) return "tablet";
  return "desktop";
}

export function useViewport(): {
  size: ViewportSize;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** True for mobile + tablet — the breakpoint at which the sidebar
   * is replaced by the bottom-bar nav. */
  isCompact: boolean;
} {
  const [size, setSize] = React.useState<ViewportSize>(() => readSize());

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const update = (): void => setSize(readSize());
    // matchMedia is cheaper than a window resize listener — the OS
    // only fires when the breakpoint actually crosses the boundary.
    const mqMobile = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const mqTablet = window.matchMedia(`(max-width: ${TABLET_MAX}px)`);
    mqMobile.addEventListener("change", update);
    mqTablet.addEventListener("change", update);
    // Pick up any resize that didn't cross a boundary (zoom / DPR change).
    window.addEventListener("resize", update);
    return () => {
      mqMobile.removeEventListener("change", update);
      mqTablet.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return {
    size,
    isMobile: size === "mobile",
    isTablet: size === "tablet",
    isDesktop: size === "desktop",
    isCompact: size !== "desktop",
  };
}
